package com.evoapps.evoshift.domain.usecase.calendar

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.calendar.ShiftCalendar
import com.evoapps.evoshift.domain.model.CalendarFeed
import com.evoapps.evoshift.domain.model.CalendarFile
import com.evoapps.evoshift.domain.model.DayRange
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterMailResult
import com.evoapps.evoshift.domain.repository.ScheduleDistributionRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.flow.first
import kotlin.time.Instant

/**
 * "Add to calendar" (SH-45): the signed-in person's published shifts of a month as an .ics file.
 * Only their own shifts, only published ones (what they see on My shifts).
 */
class ExportShiftCalendarUseCase(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(rosterId: String): Result<CalendarFile> = runCatching {
        val user = getCurrentUser().first() ?: error("signed out")
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        val role = user.engagements[workplaceId]?.role
        val events = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
            .filter { it.userId == user.uid && it.isPublished }
            .mapNotNull { shift ->
                ShiftCalendar.event(
                    shift,
                    summary = ShiftCalendar.summary(shift.requiredRole ?: role, workplace.name),
                    location = workplace.address
                )
            }
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        CalendarFile(
            name = "evoshift-$year-${month.toString().padStart(2, '0')}.ics",
            text = ShiftCalendar.build(events, workplace.settings.timezone, "EVoShift – ${workplace.name}", now()),
            events = events.size
        )
    }
}

/** "Email the roster" (SH-45): the owner or a manager sends a published month to the team or one person. */
class EmailRosterUseCase(private val repository: ScheduleDistributionRepository) {
    suspend operator fun invoke(rosterId: String, userId: String?, days: DayRange?): Result<RosterMailResult> =
        repository.emailRoster(rosterId, userId, days)

    /** SH-242: what became of the e-mails just sent. */
    suspend fun delivery(mailIds: List<String>): Result<com.evoapps.evoshift.domain.model.RosterMailDelivery> =
        repository.rosterMailDelivery(mailIds)
}

/**
 * SH-261: "Print roster": the whole team's month (one page) or a week of it (larger print) as a
 * PDF to pin up at work. The server (printRoster) checks the person owns or manages the workplace.
 */
class PrintRosterUseCase(private val repository: ScheduleDistributionRepository) {
    suspend operator fun invoke(rosterId: String, days: DayRange?, language: String): Result<com.evoapps.evoshift.domain.model.PrintedRoster> {
        if (days != null && days.toDay - days.fromDay >= WEEK_DAYS) {
            return Result.failure(IllegalArgumentException("A week is at most $WEEK_DAYS days"))
        }
        return repository.printRoster(rosterId, days, language)
    }

    companion object {
        /** The longest stretch printed as a week (functions/rosterPrint.js WEEK_DAYS). */
        const val WEEK_DAYS = 7
    }
}

/** The private calendar link (SH-45): read, create (replacing an older one) or turn off. */
class ManageCalendarFeedUseCase(private val repository: ScheduleDistributionRepository) {
    suspend fun status(): Result<CalendarFeed> = repository.calendarFeed()
    suspend fun create(): Result<CalendarFeed> = repository.createCalendarFeed()
    suspend fun revoke(): Result<CalendarFeed> = repository.revokeCalendarFeed()
}
